if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    switchByVariantTitle(variantTitle) {
      const firstAttr  = `${variantTitle}-first`;
      const viewer     = this.elements.viewer;
      const thumbsCont = this.elements.thumbnails;

      const allSlides = Array.from(viewer.querySelectorAll('[data-media-id]'));
      const allThumbs = thumbsCont ? Array.from(thumbsCont.querySelectorAll('[data-target]')) : [];

      if (!allSlides.some(el => el.dataset.title)) return false;

      const isMatch = (el) => el.dataset.title === firstAttr || el.dataset.title === variantTitle;

      const matchingSlides = allSlides.filter(isMatch);

      if (matchingSlides.length === 0) {
        allSlides.forEach(el => { el.style.display = ''; });
        allThumbs.forEach(el => { el.style.display = ''; });
        return false;
      }

      allSlides.forEach(el => {
        el.classList.remove('is-active');
        if (el.dataset.title) {
          el.style.display = isMatch(el) ? '' : 'none';
        }
      });

      const slideList = allSlides[0].parentElement;
      const heroSlide = matchingSlides.find(el => el.dataset.title === firstAttr) || matchingSlides[0];
      const otherMatching = matchingSlides.filter(el => el !== heroSlide);

      [...otherMatching].reverse().forEach(el => slideList.prepend(el));
      slideList.prepend(heroSlide);
      heroSlide.classList.add('is-active');

      this.preventStickyHeader();
      window.setTimeout(() => {
        slideList.scrollTo({ left: 0 });
        if (viewer.resetPages) viewer.resetPages();
      });
      this.playActiveMedia(heroSlide);

      if (!thumbsCont) return true;

      const thumbList = thumbsCont.querySelector('ul');

      allThumbs.forEach(el => {
        el.querySelector('button')?.removeAttribute('aria-current');
        if (el.dataset.title) {
          el.style.display = isMatch(el) ? '' : 'none';
        }
      });

      const heroThumb = thumbsCont.querySelector(`[data-target="${heroSlide.dataset.mediaId}"]`);
      const matchingThumbs = allThumbs.filter(isMatch);
      const otherMatchingThumbs = matchingThumbs.filter(el => el !== heroThumb);

      [...otherMatchingThumbs].reverse().forEach(el => thumbList?.prepend(el));
      if (heroThumb) {
        thumbList?.prepend(heroThumb);
        heroThumb.querySelector('button')?.setAttribute('aria-current', 'true');
      }

      window.setTimeout(() => {
        thumbList?.scrollTo({ left: 0 });
      });

      return true;
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        
        // Ensure accessibilityStrings exists
        if (!window.accessibilityStrings) {
          window.accessibilityStrings = {
            imageAvailable: 'Image {index} is now active in the gallery view'
          };
        }
        
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
